<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/shadcn'
import type { JTabsProps, JTabsEmits, DynamicTab } from '@/types/dynamic-tabs.types'
import { X } from 'lucide-vue-next'
import { cn } from '@/lib/utils'
import JIcon from '@/components/atoms/JIcon.vue'

/**
 * JTabs - 기본 탭 UI 컴포넌트 (molecules)
 * Basic Tabs UI Component
 *
 * @description
 * 정적인 탭 목록을 렌더링하는 기본 탭 컴포넌트입니다.
 * 닫기 버튼, 아이콘 등을 지원합니다.
 *
 * @example
 * ```vue
 * <JTabs
 *   :tabs="tabs"
 *   :active-tab-id="activeId"
 *   @tab-change="handleChange"
 *   @tab-close="handleClose"
 * />
 * ```
 */

type StyleType = 'default' | 'minimal'

const props = withDefaults(defineProps<JTabsProps>(), {
  styletype: 'default',
  keepAlive: false,
})

const emit = defineEmits<JTabsEmits>()

/**
 * 안전한 tabs 배열 (undefined/null 체크)
 * Safe tabs array (undefined/null check)
 */
const safeTabs = computed(() => {
  return Array.isArray(props.tabs) ? props.tabs : []
})

/**
 * 현재 활성화된 탭 ID (내부 상태)
 * Current active tab ID (internal state)
 */
const internalActiveId = ref<string>(
  props.activeTabId || (safeTabs.value.length > 0 ? safeTabs.value[0]?.id : '') || ''
)

/**
 * 이벤트 처리 중 플래그 (중복 이벤트 방지)
 * Flag to prevent duplicate events
 */
let isHandlingEvent = false

/**
 * props.activeTabId가 변경되면 내부 상태 동기화
 * Sync internal state when props.activeTabId changes
 */
watch(() => props.activeTabId, (newValue) => {
  if (newValue !== undefined && newValue !== internalActiveId.value) {
    internalActiveId.value = newValue
  }
}, { immediate: true })

/**
 * props.tabs가 변경되고 activeTabId가 없으면 첫 번째 탭 활성화
 * Activate first tab when tabs change and no activeTabId
 */
watch(safeTabs, (newTabs) => {
  if (!props.activeTabId && newTabs.length > 0 && !newTabs.find(t => t.id === internalActiveId.value) && newTabs[0]) {
    internalActiveId.value = newTabs[0].id
  }
})

/**
 * 탭 값 변경 핸들러 (reka-ui TabsRoot에서 직접 호출됨)
 * Tab value change handler (called directly from reka-ui TabsRoot)
 */
const handleTabValueChange = (value: string | number) => {
  if (isHandlingEvent) return

  const stringValue = String(value)
  if (stringValue !== internalActiveId.value) {
    isHandlingEvent = true
    internalActiveId.value = stringValue
    emit('update:activeTabId', stringValue)
    emit('tabChange', stringValue)
    nextTick(() => {
      isHandlingEvent = false
    })
  }
}

/**
 * 탭 클릭 핸들러 (백업 방안 - reka-ui 이벤트가 작동하지 않을 경우)
 * Tab click handler (backup - in case reka-ui events don't work)
 */
const handleTabClick = (tabId: string) => {
  if (isHandlingEvent || tabId === internalActiveId.value) return

  isHandlingEvent = true
  internalActiveId.value = tabId
  emit('update:activeTabId', tabId)
  emit('tabChange', tabId)
  nextTick(() => {
    isHandlingEvent = false
  })
}

/**
 * 탭 닫기 핸들러
 * Tab close handler
 */
const handleCloseTab = (e: Event, tabId: string) => {
  e.stopPropagation()
  emit('tabClose', tabId)
}

// ── Style Presets ──────────────────────────────────────────────────
const STYLE_PRESETS: Record<StyleType, {
  list: string
  trigger: string
  active: string
  inactive: string
  content: string
}> = {
  // Default: Card Tab 스타일 — 탭 shelf 배경 + active 탭이 content와 동일한 배경으로 "올라온" 효과
  default: {
    list: 'w-full justify-start gap-0 px-1 pt-1 bg-muted/40 border-b border-border overflow-x-auto overflow-y-hidden h-auto min-h-[2rem]',
    trigger: [
      'relative px-3 py-2 text-xs font-medium',
      'rounded-t-md -mb-px',
      'transition-all duration-200',
      'flex items-center gap-2',
    ].join(' '),
    active: [
      'data-[state=active]:text-foreground',
      'data-[state=active]:bg-background',
      'data-[state=active]:border data-[state=active]:border-border',
      'data-[state=active]:border-b-background',
      'data-[state=active]:shadow-none',
    ].join(' '),
    inactive: [
      'data-[state=inactive]:text-muted-foreground',
      'data-[state=inactive]:hover:bg-background/60',
      'data-[state=inactive]:hover:text-foreground',
    ].join(' '),
    content: '',
  },

  // Minimal: 컴팩트 Card Tab (사이드바/중첩 탭용) — 가벼운 톤 + 분리감만 추가
  minimal: {
    list: 'w-full justify-start gap-0 px-0.5 pt-0.5 bg-muted/30 border-b border-border/60 overflow-x-auto overflow-y-hidden h-auto min-h-[1.75rem]',
    trigger: [
      'relative px-2 py-1.5 text-xs font-medium',
      'rounded-t-sm -mb-px',
      'transition-all duration-200',
      'flex items-center gap-1.5',
    ].join(' '),
    active: [
      'data-[state=active]:text-foreground',
      'data-[state=active]:bg-background',
      'data-[state=active]:border data-[state=active]:border-border/60',
      'data-[state=active]:border-b-background',
      'data-[state=active]:shadow-none',
    ].join(' '),
    inactive: [
      'data-[state=inactive]:text-muted-foreground',
      'data-[state=inactive]:hover:bg-background/50',
      'data-[state=inactive]:hover:text-foreground',
    ].join(' '),
    content: '',
  },
}

const preset = computed(() => {
  return STYLE_PRESETS[props.styletype] ?? STYLE_PRESETS.default
})

// ── Computed Classes ──────────────────────────────────────────────
const rootClasses = computed(() => {
  return cn('flex flex-col w-full h-full', props.class)
})

const listClasses = computed(() => {
  return cn(preset.value.list, props.listClass)
})

const triggerClasses = computed(() => {
  return cn(
    preset.value.trigger,
    preset.value.active,
    preset.value.inactive,
  )
})

const contentWrapperClasses = computed(() => {
  return cn('flex-1 w-full overflow-auto', preset.value.content)
})

const shouldKeepAlive = (tab: DynamicTab) => tab.keepAlive ?? props.keepAlive
</script>

<template>
  <Tabs
    :model-value="internalActiveId"
    orientation="horizontal"
    :class="rootClasses"
    @update:model-value="handleTabValueChange"
  >
    <!-- 탭 헤더 영역 / Tab Headers -->
    <TabsList :class="listClasses">
      <TabsTrigger
        v-for="tab in safeTabs"
        :key="tab.id"
        :value="tab.id"
        :class="triggerClasses"
        @click="handleTabClick(tab.id)"
      >
        <!-- 탭 아이콘 / Tab Icon -->
        <JIcon
          v-if="tab.icon"
          :name="tab.icon"
          size="sm"
          class="flex-shrink-0"
        />

        <!-- 탭 레이블 / Tab Label -->
        <span class="flex-1 truncate">{{ tab.label }}</span>

        <!-- 닫기 버튼 / Close Button -->
        <button
          v-if="tab.closable"
          type="button"
          class="flex-shrink-0 h-3.5 w-3.5 rounded-sm hover:bg-destructive/10 hover:text-destructive transition-colors focus:outline-none focus:ring-2 focus:ring-ring flex items-center justify-center"
          :aria-label="`${tab.label} 탭 닫기`"
          @click="(e) => handleCloseTab(e, tab.id)"
        >
          <X class="h-2.5 w-2.5" />
        </button>
      </TabsTrigger>
    </TabsList>

    <!-- 탭 콘텐츠 영역 / Tab Contents -->
    <div :class="contentWrapperClasses">
      <TabsContent
        v-for="tab in safeTabs"
        :key="`content-${tab.id}`"
        :value="tab.id"
        :force-mount="shouldKeepAlive(tab) || undefined"
        class="h-full mt-0 data-[state=active]:flex data-[state=active]:flex-col data-[state=active]:animate-tab-fade-in data-[state=inactive]:hidden"
      >
        <KeepAlive v-if="shouldKeepAlive(tab)">
          <slot :name="`content-${tab.id}`" :tab="tab">
            <component
              :is="tab.component"
              v-if="tab.component"
              v-bind="tab.props || {}"
            />
            <div v-else class="p-4">
              <p class="text-muted-foreground">{{ tab.label }} 콘텐츠</p>
            </div>
          </slot>
        </KeepAlive>
        <template v-else>
          <slot :name="`content-${tab.id}`" :tab="tab">
            <component
              :is="tab.component"
              v-if="tab.component"
              v-bind="tab.props || {}"
            />
            <div v-else class="p-4">
              <p class="text-muted-foreground">{{ tab.label }} 콘텐츠</p>
            </div>
          </slot>
        </template>
      </TabsContent>
    </div>
  </Tabs>
</template>

<style scoped>
/* ── 스크롤바: Tailwind으로 불가능한 pseudo-element만 `:deep()` 사용 ── */
:deep([role="tablist"]) {
  scrollbar-width: thin;
  scrollbar-color: hsl(var(--border)) transparent;
}

:deep([role="tablist"]::-webkit-scrollbar) {
  height: 4px;
}

:deep([role="tablist"]::-webkit-scrollbar-track) {
  background: transparent;
}

:deep([role="tablist"]::-webkit-scrollbar-thumb) {
  background-color: hsl(var(--border));
  border-radius: 2px;
}

:deep([role="tablist"]::-webkit-scrollbar-thumb:hover) {
  background-color: hsl(var(--muted-foreground) / 0.4);
}

/* ── 콘텐츠 전환 애니메이션 ── */
@keyframes tab-fade-in {
  from {
    opacity: 0;
    transform: translateY(2px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

:deep(.animate-tab-fade-in) {
  animation: tab-fade-in 0.15s ease-out;
}
</style>
